import React, { useState } from 'react';
import Seo from '../components/Seo';
import { Reveal, MaskLine, Eyebrow } from '../components/Reveal';
import Marquee from '../components/Marquee';
import { cleaningLevels, washingProcesses, dryingProcesses, industries, img } from '../data/content';
import { pageSeo, breadcrumbSchema } from '../data/seo';

function Levels() {
  return (
    <section className="py-20 lg:py-28" data-testid="industries-levels">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Levels of Component Cleaning</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-3xl">
            From visual cleanliness to Millipore-verified precision.
          </h2>
        </Reveal>

        <div className="mt-14 space-y-px bg-steelline/50 border border-steelline/50">
          {cleaningLevels.map((lvl, i) => (
            <Reveal key={lvl.num} delay={i * 0.1} testid={`cleaning-level-${lvl.num}`}>
              <div className="grid lg:grid-cols-12 gap-6 bg-ink p-8 lg:p-10 hover:bg-card transition-colors duration-300">
                <div className="lg:col-span-2">
                  <p className="font-display font-extrabold text-5xl text-outline">{lvl.num}</p>
                </div>
                <div className="lg:col-span-6">
                  <h3 className="font-display font-bold text-xl sm:text-2xl text-paper">{lvl.name}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-skyc">{lvl.tagline}</p>
                  <ul className="mt-5 space-y-2.5">
                    {lvl.points.map((pt) => (
                      <li key={pt} className="flex gap-3 text-sm text-fog items-start">
                        <span className="text-flame mt-[3px]" aria-hidden="true">▸</span>{pt}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-4">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-flame mb-4">Measurement</p>
                  <div className="flex flex-col gap-3">
                    {lvl.measurement.map((m) => (
                      <div key={m} className="border border-steelline bg-surface/60 px-4 py-3 text-sm text-paper" data-testid={`level-${lvl.num}-measurement`}>{m}</div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Processes() {
  return (
    <section className="py-20 lg:py-28 bg-surface/40 border-y border-steelline/60" data-testid="industries-processes">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Introduction — Cleaning Processes</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-2xl">
            Wash, rinse, dry — engineered process by process.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-8">
          <Reveal testid="process-panel-washing">
            <div className="h-full bg-ink border border-steelline/60 p-8 lg:p-10">
              <h3 className="font-display font-bold text-xl text-paper">Washing Processes</h3>
              <div className="mt-6 flex flex-wrap gap-3">
                {washingProcesses.map((p, i) => (
                  <span key={p} className="border border-steelline bg-surface/60 px-4 py-2.5 text-sm text-paper hover:border-flame hover:text-flame transition-colors" data-testid={`washing-process-${i}`}>{p}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} testid="process-panel-drying">
            <div className="h-full bg-ink border border-steelline/60 p-8 lg:p-10">
              <h3 className="font-display font-bold text-xl text-paper">Drying Processes</h3>
              <div className="mt-6 flex flex-wrap gap-3">
                {dryingProcesses.map((p, i) => (
                  <span key={p} className="border border-steelline bg-surface/60 px-4 py-2.5 text-sm text-paper hover:border-flame hover:text-flame transition-colors" data-testid={`drying-process-${i}`}>{p}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function IndustryMatrix() {
  const [active, setActive] = useState(0);
  const ind = industries[active];
  return (
    <section className="py-20 lg:py-28" data-testid="industries-matrix">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Diverse Range of Components Cleaned</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-2xl">
            Every powertrain part. Every industry.
          </h2>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="flex flex-wrap gap-2" data-testid="industry-tabs">
            {industries.map((ind2, i) => (
              <button
                key={ind2.id}
                onClick={() => setActive(i)}
                data-testid={`industry-tab-${ind2.id}`}
                className={`px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors duration-200 ${i === active ? 'border-flame text-flame bg-flame/10' : 'border-steelline text-fog hover:text-paper hover:border-fog'}`}
              >
                {ind2.name}
              </button>
            ))}
          </div>

          <div className="mt-8 grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3" data-testid="industry-components">
              {ind.components.map((c) => (
                <div key={c} className="flex items-center gap-3 border border-steelline bg-surface/50 px-5 py-4 hover:border-flame/60 transition-colors" data-testid={`component-chip-${c.toLowerCase().replace(/[^a-z]+/g, '-').replace(/(^-|-$)/g, '')}`}>
                  <span className="h-1.5 w-1.5 bg-flame shrink-0" aria-hidden="true" />
                  <span className="text-sm text-paper">{c}</span>
                </div>
              ))}
            </div>
            <div className="clip-notch border border-steelline/80 overflow-hidden">
              <img
                key={ind.id}
                src={img(['rotary-1.jpg', 'frontload-3.jpg', 'throughfeed-4.jpg', 'handling-2.jpg'][active])}
                alt={`${ind.name} components cleaning`}
                className="w-full aspect-[4/3] object-cover"
                data-testid="industry-active-image"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Industries() {
  return (
    <>
      <Seo {...pageSeo.industries} schemas={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Industries & Process', path: '/industries' }])]} />
      <section className="relative pt-40 pb-20 blueprint-grid overflow-hidden" data-testid="industries-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 to-ink" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8 relative z-10">
          <Eyebrow className="mb-6 flex items-center gap-3">
            <span className="inline-block h-[1px] w-10 bg-flame" aria-hidden="true" /> Industries & Process
          </Eyebrow>
          <h1 className="font-display font-extrabold text-paper leading-[0.98] tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            <MaskLine delay={0.15}>WASH. RINSE.</MaskLine>
            <MaskLine delay={0.3}><span className="text-flame">PROVEN DRY.</span></MaskLine>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-fog leading-relaxed">
            Cleaning systems designed for powertrain and machined parts across two-wheeler, passenger vehicle,
            commercial vehicle and general engineering industries.
          </p>
        </div>
      </section>

      <Levels />
      <Processes />
      <IndustryMatrix />
      <Marquee />
    </>
  );
}
